import styled, { css } from 'styled-components';
import { select, withQuery } from '@panel/commons/core/themev2';
import { NumericInput } from '@panel/commons/lib/inputs';
import { getColor } from '@panel/commons/core/themev3';
import AlertCircle from '@panel/commons/assets/icons/AlertCircleBlue.svg';

const SubWrapper = styled.div`
  padding: 40px 40px 45px;
  ${withQuery.lg.down()} {
    padding: 40px 20px 45px;
  }
  ${withQuery.xs.down()} {
    padding: 0px 0px 70px;
    margin: 0px 16px;
  }
  input,
  textarea {
    &.error {
      border-color: ${select('negative')};
    }
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

const ModuleTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  padding: 0px 0px 12px;
  ${withQuery.xs.down()} {
    font-size: 18px;
    padding-top: 28px;
  }
  &.withSubTitle {
    padding: 28px 0 0 0;
  }
`;

const ModuleSubTitle = styled.div`
  font-size: 14px;
  padding: 0px 0px 30px;
  color: ${select('gray50')};
  white-space: normal;
  ${withQuery.xs.down()} {
    padding: 0px 0px 24px;
  }
`;

const FeatureContent = styled.div`
  width: auto;
  max-width: 324px;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  margin-bottom: 40px;
  position: relative;
  &.selectFeature > div {
    border-bottom: 0;
  }
  & > div.errorWrapper {
    max-width: 128px;
    width: 100%;
  }
  &:nth-child(2n) {
    margin: 0px 0px 40px;
  }
  ${withQuery.xs.down()} {
    display: block;
    max-width: 100%;
    &:nth-child(2n) {
      margin: 0px 0px 0px;
    }
    & > div.errorWrapper {
      max-width: 100%;
      margin-bottom: 20px;
      white-space: break-spaces;
    }
  }
`;

const ContentDescription = styled(FeatureContent)`
  width: auto;
`;

const FeatureOptions = styled.div`
  margin-bottom: 25px;
  position: relative;
  height: 66px;
  width: 128px;
  ${withQuery.xs.down()} {
    width: 100%;
    display: flex;
    justify-content: inherit;
    border-bottom: 1px solid ${select('middleGray')};
    height: 77px;
    margin: 25px 0px 0px;
    &:first-child {
      margin: 0px;
    }
  }
`;

const AreaOptions = styled(FeatureOptions)`
  ${withQuery.xs.down()} {
    flex-direction: column;
    height: auto;
  }
`;

const PriceOptions = styled(AreaOptions)`
  ${withQuery.xs.down()} {
    margin: 0px;
  }
`;

const FeatureLabel = styled.label`
  display: block;
  margin-bottom: 10px;
  ${withQuery.xs.down()} {
    white-space: break-spaces;
    display: flex;
    align-items: center;
  }
`;

const ButtonRight = styled.div`
  width: 32px;
  height: 32px;
  background: ${select('middleGray')};
  border-radius: 4px;
  position: absolute;
  right: 4px;
  top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  :hover {
    background: ${select('midLightGray')};
  }
  &.disabled {
    background: ${select('lightGray')};
    color: ${select('midLightGray')};
    cursor: not-allowed;
    :active {
      pointer-events: none;
    }
  }
  ${withQuery.xs.down()} {
    width: 48px;
    height: 48px;
    font-size: 22px;
    border-radius: 12px;
  }
`;

const ButtonLeft = styled(ButtonRight)`
  left: 4px;
`;

const Options = styled.div`
  position: relative;
  ${withQuery.xs.down()} {
    display: inline-block;
  }
`;

const IconWrapper = styled.div`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 12px;
  left: 40px;
  float: right;
  pointer-events: none;
  ${withQuery.xs.down()} {
    left: 63px;
    top: 16px;
  }
  ${withQuery.xxs.down()} {
    top: 10px;
    font-size: 23px;
  }
`;

const Option = styled.option`
  :disabled {
    color: ${select('gray50')};
  }
`;

const commonInput = css`
  border: 1px solid ${select('middleGray')};
  border-radius: 8px;
  width: 128px;
  height: 40px;
  text-align: center;
  :focus {
    outline: none;
  }
  ${withQuery.xs.down()} {
    height: 56px;
    width: 167px;
    border-radius: 13px;
  }
`;

const FeatureInput = styled.input`
  ${commonInput}
`;

const FeatureNumericInput = styled(NumericInput)`
  ${commonInput}
`;

const InputBox = styled(FeatureInput)`
  text-align: center;
  padding: 0px 0px 0px 66px;
  ${withQuery.xs.down()} {
    width: 100%;
    height: 46px;
    padding: 0px 0px 0px 166px;
    border-radius: 8px;
  }
`;

const DropdownInput = styled(InputBox)`
  :focus {
    border: 1px solid ${getColor('primary')};
  }
`;
const Select = styled.select`
  width: 63px;
  height: 90%;
  background: ${select('lightGray')};
  border-radius: 8px 0 0px 8px;
  margin: 1px 0 0 1px;
  position: absolute;
  left: 1px;
  top: 1px;
  padding-left: 15px;
  cursor: pointer;
  -webkit-appearance: initial;
  :focus {
    outline: none;
  }
  :focus + input {
    border: 1px solid ${getColor('primary')};
  }
  ${withQuery.xs.down()} {
    width: 137px;
    height: 44px;
    top: 1px;
    left: 1px;
    padding-left: 44px;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    margin: 0px;
  }
`;

const InputWrapper = styled.div`
  position: absolute;
  top: 30px;
  right: 50%;
  margin-right: -80px;
  > span {
    position: absolute;
  }
`;

const InputQuantity = styled(FeatureNumericInput)`
  width: 80px;
  height: 49px;
  padding: 0px;
  text-align: center;
  :focus {
    border: 1px solid ${getColor('primary')};
    outline: none;
  }
  ${withQuery.xs.down()} {
    width: 80px;
    height: 48px;
    position: absolute;
    left: 50%;
    margin-left: -30px;
    border-radius: 8px;
    & + span {
      top: 40px;
      right: -80px;
    }
  }
  ${withQuery.xxs.down()} {
    width: 80px;
    margin: 0px;
    margin-left: -20px;
  }
`;

const PropertyDescription = styled.div`
  ${withQuery.xs.down()} {
    width: 100%;
  }
  > .titleContent {
    margin-bottom: 30px;
  }
`;

const InputTitle = styled.input`
  width: 345px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${select('gray25')};
  text-indent: 10px;
  :focus {
    border: 1px solid ${getColor('primary')};
    outline: none;
  }
  ${withQuery.xs.down()} {
    height: 48px;
    width: 100%;
  }
`;

const InputDescription = styled.textarea`
  height: 128px;
  width: 345px;
  border: 1px solid ${select('gray25')};
  border-radius: 8px;
  padding: 8px;
  margin-bottom: -6px;
  :focus {
    border: 1px solid ${getColor('primary')};
    outline: none;
  }
  ${withQuery.xs.down()} {
    width: 100%;
  }
`;

const Back = styled.button`
  background: transparent;
  font-weight: 600;
  margin-right: 60px;
  font-family: 'Hind';
  font-size: 1.4rem;
  position: absolute;
  bottom: 30px;
  right: 140px;
  ${withQuery.md.down()} {
    display: none;
  }
`;

const ErrorMessage = styled.span`
  color: ${select('negative')};
  display: inherit;
  padding: 7px 0px 0px 15px;
  font-size: 12px;
  ${props =>
    props.longText &&
    css`
      width: 220px;
      white-space: break-spaces;
    `}
`;

const IncrementableInputContainer = styled.div`
  margin-bottom: 25px;
  ${withQuery.sm.down()} {
    width: 100%;
  }
`;
const DescriptionCount = styled.p`
  align-self: end;
`;

const FooterDescriptionContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  ${props =>
    !props.error &&
    css`
      justify-content: end;
    `}}
`;

const InfoBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  padding: 12px;
  border-radius: 4px;
  white-space: normal;
  background-color: ${select('lightGray')};
  margin-bottom: 14px;
  &:before {
    content: '';
    vertical-align: text-top;
    width: 16px;
    height: 16px;
    margin-right: 9px;
    position: absolute;
    background-image: url(${AlertCircle});
    background-repeat: no-repeat;
    top: 14px;
    ${withQuery.xs.down()} {
      align-items: baseline;
      top: 16px;
    }
  }
  p {
    margin-left: 24px;
  }

  ${withQuery.xs.down()} {
    margin-bottom: 24px;
  }
  ${withQuery.lg.down()} {
    margin: 0 16px;
    width: auto;
  }
`;

export {
  SubWrapper,
  ModuleTitle,
  ModuleSubTitle,
  FeatureContent,
  ContentDescription,
  FeatureOptions,
  AreaOptions,
  PriceOptions,
  FeatureLabel,
  ButtonRight,
  ButtonLeft,
  Options,
  IconWrapper,
  Option,
  FeatureInput,
  DropdownInput,
  InputBox,
  Select,
  InputWrapper,
  InputQuantity,
  PropertyDescription,
  InputTitle,
  InputDescription,
  Back,
  ErrorMessage,
  IncrementableInputContainer,
  DescriptionCount,
  FooterDescriptionContainer,
  InfoBox
};
